/**
 * Centralised entity-type identity (D-075 follow-up).
 *
 * Single source of truth for "what kind of entity is this" — the labels/dots that
 * used to be copy-pasted per component (EntitySwitcher's dot map, Investor Outreach's
 * kind labels, EntityDetailPage's own TYPE_CONFIG). Human labels come from the wizard
 * types SSOT (ENTITY_TYPE_LABELS); this module adds the canonical type dot + icon so
 * every page shows the same company / product / person… chip.
 *
 * Corporate = the entity types wizards scope their work to (company / organisation /
 * governance_body) — everything else (product, service, person…) is "other".
 */
import {
  Award,
  Building2,
  Cpu,
  GitBranch,
  Package,
  User,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { ENTITY_TYPE_LABELS } from "@/components/wizard/types";

export const CORPORATE_TYPES = ["company", "organisation", "governance_body"] as const;

export const isCorporateType = (t?: string): boolean =>
  (CORPORATE_TYPES as readonly string[]).includes((t ?? "").toLowerCase());

const TYPE_KEY = (t?: string) => (t ?? "").toLowerCase();

export interface EntityTypeMeta {
  /** Tailwind dot colour class (the canonical "kind" marker). */
  dot: string;
  /** Tailwind text colour class for an emphasised icon/label. */
  text: string;
  icon: LucideIcon;
}

export const ENTITY_TYPE_META: Record<string, EntityTypeMeta> = {
  company:         { dot: "bg-blue-500",       text: "text-blue-600 dark:text-blue-400",       icon: Building2 },
  product:         { dot: "bg-violet-500",     text: "text-violet-600 dark:text-violet-400",   icon: Package },
  service:         { dot: "bg-cyan-500",       text: "text-cyan-600 dark:text-cyan-400",       icon: Wrench },
  platform:        { dot: "bg-indigo-500",     text: "text-indigo-600 dark:text-indigo-400",   icon: Cpu },
  person:          { dot: "bg-emerald-500",    text: "text-emerald-600 dark:text-emerald-400", icon: User },
  organisation:    { dot: "bg-amber-500",      text: "text-amber-600 dark:text-amber-400",     icon: Users },
  governance_body: { dot: "bg-red-500",        text: "text-red-600 dark:text-red-400",         icon: Award },
  function:        { dot: "bg-gray-500",       text: "text-gray-500 dark:text-gray-400",       icon: GitBranch },
};

const FALLBACK_META: EntityTypeMeta = { dot: "bg-slate-400", text: "text-muted-foreground", icon: Building2 };

/** Human label for an entity type ("Product", "Governing Body", …). */
export const entityTypeLabel = (t?: string): string =>
  ENTITY_TYPE_LABELS[TYPE_KEY(t)] ?? (t ? `${t[0]?.toUpperCase() ?? ""}${t.slice(1)}` : "Entity");

export const entityTypeMeta = (t?: string): EntityTypeMeta =>
  ENTITY_TYPE_META[TYPE_KEY(t)] ?? FALLBACK_META;

export const entityTypeDotClass = (t?: string): string => entityTypeMeta(t).dot;

/** Small coloured type dot — the canonical "kind" marker used in entity lists. */
export function EntityTypeDot({ type, className = "" }: { type?: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={`h-2 w-2 shrink-0 rounded-full ${entityTypeDotClass(type)} ${className}`}
    />
  );
}

/** Type icon (Building2 for company, Package for product, …). */
export function EntityTypeIcon({ type, className = "h-4 w-4" }: { type?: string; className?: string }) {
  const { icon: Icon } = entityTypeMeta(type);
  return <Icon className={className} />;
}
